import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "peptichill_admin_token";

const AdminAuthContext = createContext(null);

/**
 * Axios instance for all admin requests. Injects Bearer token; on 401 it
 * clears the token + sends user to /admin/login.
 */
export const adminApi = axios.create({ baseURL: API });

let _onAuthLost = null;
export function setAuthLostHandler(fn) { _onAuthLost = fn; }

adminApi.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

adminApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      if (_onAuthLost) _onAuthLost();
    }
    return Promise.reject(err);
  },
);

export function AdminAuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = anon, obj = authed
  const [authNonce, setAuthNonce] = useState(0);

  const refresh = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setUser(null); return; }
    try {
      const res = await adminApi.get("/admin/me");
      setUser(res.data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh, authNonce]);

  useEffect(() => {
    setAuthLostHandler(() => setUser(null));
    return () => setAuthLostHandler(null);
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await adminApi.post("/admin/login", { username, password });
    localStorage.setItem(TOKEN_KEY, res.data.token);
    setAuthNonce((n) => n + 1);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  return (
    <AdminAuthContext.Provider value={{ user, login, logout, refresh }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside <AdminAuthProvider>");
  return ctx;
}
